import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (/** @type {import('@theme/events').CartUpdateEvent} */ event) => {
    // Don't open drawer for quick-add interactions
    const target = /** @type {Element | null} */ (event.target);
    const isFromQuickAdd = event.detail.data?.source === 'product-form-component' &&
      target?.closest('quick-add-component');

    // Also check for direct quick-add component updates (from +/- buttons)
    // The quick-add.js uses fetch directly, so it might not have the same source
    // But if the event was dispatched by the server response handling, we might need to check the trigger
    // However, quick-add.js does NOT dispatch an event with source='product-form-component'
    // It relies on the global cart update event.
    // We can check if the active element is within a quick-add component
    const activeElement = document.activeElement;
    const isQuickAddInteraction = activeElement?.closest('quick-add-component');

    if (this.hasAttribute('auto-open') && !isFromQuickAdd && !isQuickAddInteraction) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
